import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FirstTimePasswordModal from './FirstTimePasswordModal';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, loading, clerkIsLoaded, clerkIsSignedIn, needsPasswordSetup, refreshPasswordStatus } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [syncTimeout, setSyncTimeout] = useState(false);

  // Show password modal if user needs to set password
  useEffect(() => {
    if (clerkIsLoaded && clerkIsSignedIn && user && needsPasswordSetup) {
      setShowPasswordModal(true);
    } else if (!needsPasswordSetup) {
      setShowPasswordModal(false);
    }
  }, [clerkIsLoaded, clerkIsSignedIn, user, needsPasswordSetup]);

  // Handle sync timeout - if sync takes more than 10 seconds, something's wrong
  useEffect(() => {
    if (clerkIsSignedIn && !user) {
      // Set a timeout - if sync takes more than 10 seconds, something's wrong
      const timer = setTimeout(() => {
        console.warn('⚠️ User sync taking too long, setting timeout flag');
        setSyncTimeout(true);
      }, 10000);
      
      return () => clearTimeout(timer);
    } else {
      setSyncTimeout(false);
    }
  }, [clerkIsSignedIn, user]);

  const handlePasswordComplete = async () => {
    // Trigger refresh of password status in AuthContext
    refreshPasswordStatus();
    // Small delay to ensure state updates
    setTimeout(() => {
      setShowPasswordModal(false);
    }, 100);
  };

  // Show loading while auth is initializing
  if (!clerkIsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If signed in but we don't have user data yet, wait for sync
  // Don't redirect to login while sync is in progress
  if (clerkIsSignedIn && !user && !syncTimeout) {
    // Show loading while syncing
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Syncing user data...</p>
        </div>
      </div>
    );
  }
  
  // If we're loading and not signed in, show loading
  // But if we're loading and signed in, we already handled it above
  if (loading && !clerkIsSignedIn) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If not signed in, redirect to login
  if (!clerkIsSignedIn) {
    return <Navigate to="/login" replace />;
  }

  // If signed in, show content
  return (
    <>
      {/* Show password modal if needed - blocks access until password is set */}
      <FirstTimePasswordModal
        isOpen={showPasswordModal}
        onComplete={handlePasswordComplete}
      />
      {/* Only show children if password modal is not open */}
      {!showPasswordModal && <>{children}</>}
    </>
  );
};

export default PrivateRoute;
